import {
  Autocomplete,
  Box,
  Grid,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import getAxiosError from "../../../../app/api/axiosError";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import SubmitModalWithFormik from "../../../../components/common/modalPopup/SubmitModalWithFormik";
import {
  AddRateCardRequest,
  AddRatecardClientMapping,
} from "../dtos/AddRateCardRequest";
import { RATE_CARD_TYPES } from "../RateCardConstants";
import { useEffect, useState } from "react";
import { fetchStoreRateCardAssociationList } from "../../../../app/features/ratecard/ratecardSlice";
import { ParcelCenterRateCardAssociationResponse } from "../dtos/StoreRatecardAssociationResponse";
import { useSubmitModal } from "../../../../components/common/modalPopup/useSubmitModal";
import AddRouteRateModal from "../routeRate/AddRouteRateModal";
import Loader from "../../../../components/common/loader/Loader";
import { mapSnakeCaseToCapitalCaseWithSpaces } from "../../../../components/common/utils/StringUtils";

// type to recieve parameter for this component to initialize
interface AddRatecardModalProps {
  clientId: number;
  clientType: string;
  clientName: string;
  closeAddRatecardModal: CallableFunction;
  onCompleted: CallableFunction;
}

interface AddRateCardUiRequest {
  ratecardName: string;
  ratecardType: string;
  storeIds: number[];
  // parcelCenterIds: number[];
}

// Add new ratecard modal component definition
const AddRatecardModal = (props: AddRatecardModalProps) => {
  const addInitialValues: AddRateCardUiRequest = {
    ratecardName: "",
    ratecardType: "",
    storeIds: [],
    // parcelCenterIds: [],
  };

  const ratecardNamePrefix =
    props.clientName && props.clientName.length >= 3
      ? props.clientName.substring(0, 3).toUpperCase()
      : "DEF"; // DEF is the default prefix

  const dispatch = useAppDispatch();

  const [addRatecardUIRequest, setAddRatecardUIRequest] =
    useState(addInitialValues);

  useEffect(() => {
    fetchStoreRateCardAssociationData();
  }, []);

  // get overview data of store
  const fetchStoreRateCardAssociationData = async () => {
    try {
      const response = await dispatch(
        fetchStoreRateCardAssociationList(props.clientId)
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "storeRateCardAssociationList",
        });
        return;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "storeRateCardAssociationException",
      });
    } finally {
      // setDataFetchig(false);
    }
  };

  // import store data from "Redux-Store"
  const { isLoading: isStoresLoading, storeRateCardAssociationList } =
    useSelector((state: RootState) => state.ratecard);

  const parcelCentersRateCardAssociationList: ParcelCenterRateCardAssociationResponse[] =
    []; // TODO: to be implemented for parcel centers

  // validation rule while adding new ratecard into the database
  const validationSchema = Yup.object().shape({
    ratecardName: Yup.string().required("Ratecard name is required"),
    ratecardType: Yup.string().required("Ratecard type is required"),
    storeIds: Yup.array().min(1, "Pick at least one store").of(Yup.number()),
  });

  const mapUiRequestToServerRequest = (
    uiRequest: AddRateCardUiRequest
  ): AddRateCardRequest => {
    let serverRequest: AddRateCardRequest = {
      ratecardName: uiRequest.ratecardName,
      ratecardType: uiRequest.ratecardType.toLowerCase(),
      ratecardClientMappings: [],
      ratecardValues: {
        baseRate: 0,
        variableRate: 0,
        // variableRateType: undefined,
        routeMinimum: 0,
        lateCancellationCharge: 0,
        // lateCancellationType: undefined,
      },
      rateCardCustomFieldsData:[],
  
      ratecardFuelSurchargeFields: [],
      ratecardFuelSurchargeLookup: [],
      ratecardPeakSurcharges: [],
      createdBy: "",
      updatedBy: "",
    };

    let storeIds: AddRatecardClientMapping[] = [];
    let parcelCenters: AddRatecardClientMapping[] = [];

    uiRequest.storeIds.forEach((storeId) => {
      storeIds.push({
        storeId: storeId,
      });
    });

    // TODO: add parcel centers also

    const ratecardClientMappings = storeIds.concat(parcelCenters);

    serverRequest.ratecardClientMappings = ratecardClientMappings;
    return serverRequest;
  };

  //on form submit make server call to persist the rate card
  const handleSubmit = async (addRatecardDetails: AddRateCardUiRequest) => {
    addRatecardDetails.ratecardName = `${ratecardNamePrefix}_${addRatecardDetails.ratecardName}`;
    setAddRatecardUIRequest(addRatecardDetails);
    openRouteRateModal();
    return true;
  };

  // open  modal logic to open update route rate modal
  const [addRouteRateModal, openRouteRateModal] = useSubmitModal(
    (closeRouteRateModal) => (
      <AddRouteRateModal
        clientId={props.clientId} 
        ratecardData={null}
        ratecardRequest={mapUiRequestToServerRequest(addRatecardUIRequest)}
        closeAddRouteRateModal={closeRouteRateModal}
        onCompleted={() => {
          props.closeAddRatecardModal();
        }}
      />
    )
  );

  const storeAndParcelCenterAssociationList = storeRateCardAssociationList.map(
    (storeAssociation) => {
      return {
        ...storeAssociation,
        group: "Stores",
        isStore: true,
        nameIdentifier: storeAssociation.storeId, // adding name Identifier as stores and parcel centers don't have a common attribute for the name
      };
    }
  );

  // TODO: handle adding parcel centers later, need to have a common type for this combined list!
  // if (parcelCentersRateCardAssociationList.length > 0) {
  //   storeRateCardAssociationList.push(
  //     ...parcelCentersRateCardAssociationList.map((parcelCenterAssociation) => {
  //       return {
  //         ...parcelCenterAssociation,
  //         group: "Parcel Centers",
  //         isStore: false,
  //         nameIdentifier: parcelCenterAssociation.locationName,
  //       };
  //     })
  //   );
  // }

  return (
    <>
      <Formik
        initialValues={{ ...addInitialValues }}
        validationSchema={validationSchema}
        onSubmit={(values: AddRateCardUiRequest, { setSubmitting }) => {
          setSubmitting(true);
          handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
        }) => {
          return (
            <SubmitModalWithFormik
              title="Add New Rate Card"
              closeSubmitModal={props.closeAddRatecardModal}
              childErrorMessage={""}
              allowSubmit={false}
              size="xs"
              isLoading={false}
              submitButtonName="Next"
              closeModalAfterSubmit={false}
              fullWidth={false}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Loader isLoading={isStoresLoading || isSubmitting} />
                <Form>
                  <Grid container>
                    <Grid item>
                      <FormikTextField
                        id="isEditEnabled"
                        name="isEditEnabled"
                        label=""
                        size="small"
                        fullWidth
                        variant={"outlined"}
                        value={null}
                        sx={{ display: "none" }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <FormikTextField
                        margin="normal"
                        id="ratecardName"
                        name="ratecardName"
                        label="Rate Card Name"
                        size="small"
                        fullWidth
                        variant={"outlined"}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {`${ratecardNamePrefix}_`}
                            </InputAdornment>
                          ),
                        }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Autocomplete
                        multiple
                        id="storeIds"
                        options={storeAndParcelCenterAssociationList}
                        groupBy={(option) => option.group}
                        limitTags={3}
                        disableCloseOnSelect
                        size="small"
                        onChange={(event, value) =>
                          setFieldValue(
                            "storeIds",
                            value.map((x) => x.id)
                          )
                        }
                        value={storeAndParcelCenterAssociationList.filter((x) =>
                          values.storeIds.includes(x.id)
                        )}
                        getOptionDisabled={(option) => option.isAssociated}
                        getOptionLabel={(option) =>
                          `${option.nameIdentifier} ${
                            option.isAssociated
                              ? ` - ${option.ratecardName}`
                              : ``
                          }`
                        }
                        sx={{
                          // overriding font-family else it choses different font-family on its own!
                          fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
                        }}
                        renderInput={(params) => (
                          <FormikTextField
                            {...params}
                            margin="normal"
                            name="storeIds"
                            label={
                              props.clientType === "both"
                                ? "Associated Parcel Centers/Stores"
                                : props.clientType === "e2e"
                                ? "Associated Parcel Centers"
                                : "Associated Stores"
                            }
                            size="small"
                            fullWidth
                            variant={"outlined"}
                            required
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <FormikTextField
                        select
                        margin="normal"
                        id="ratecardType"
                        name="ratecardType"
                        label="Rate Card Type"
                        size="small"
                        fullWidth
                        variant={"outlined"}
                        required
                      >
                        {RATE_CARD_TYPES.map((ratecardType, index) => (
                          <MenuItem
                            key={index}
                            value={ratecardType}
                            disabled={ratecardType !== "route_rate"}
                          >
                            {mapSnakeCaseToCapitalCaseWithSpaces(ratecardType)}
                          </MenuItem>
                        ))}
                      </FormikTextField>
                    </Grid>
                  </Grid>
                </Form>
              </Box>
            </SubmitModalWithFormik>
          );
        }}
      </Formik>
      {addRouteRateModal}
    </>
  );
};

export default AddRatecardModal;
